import { useState, useEffect, useCallback } from 'react';
import { useAuth }           from '@/contexts/AuthContext';
import FilterBar             from '@/components/FilterBar';
import type { FilterField }  from '@/components/FilterBar';
import PaginationBar         from '@/components/PaginationBar';
import SortableTh            from '@/components/SortableTh';
import AnbiyamFormModal      from '@/components/AnbiyamFormModal';
import ExportButton          from '@/components/ExportButton';
import type { ExportScope }  from '@/components/ExportButton';
import { useSort }           from '@/hooks/useSort';
import { exportData }        from '@/utils/export';
import type { ExportFormat } from '@/utils/export';
import {
  listAnbiyam, createAnbiyam, updateAnbiyam, deleteAnbiyam, getAnbiyamSubscriptionProgress,
} from '@/api/anbiyam';
import type { Anbiyam, AnbiyamPayload, AnbiyamSubscriptionProgress } from '@/api/anbiyam';

const ZONE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8].map((z) => ({
  label: `Zone ${z}`,
  value: z,
}));

type ProgressBand = 'lt25' | '25to50' | '50to75' | '75plus';
const PROGRESS_OPTIONS: { label: string; value: ProgressBand }[] = [
  { label: 'Below 25%', value: 'lt25' },
  { label: '25% – 50%', value: '25to50' },
  { label: '50% – 75%', value: '50to75' },
  { label: '75% and above', value: '75plus' },
];

const FILTER_FIELDS: FilterField[] = [
  { key: 'search',   label: 'Name / Code', type: 'text', placeholder: 'Search anbiyam…' },
  { key: 'zone',     label: 'Zone',        type: 'select', options: ZONE_OPTIONS },
  { key: 'progress', label: 'Subscription Progress', type: 'select', options: PROGRESS_OPTIONS },
];

const DEFAULT_FILTERS = {
  search:   null as null | string,
  zone:     null as null | number,
  progress: null as null | ProgressBand,
};

const progressPct = (p?: AnbiyamSubscriptionProgress) =>
  p && p.expectedAmount > 0 ? Math.min(100, Math.round((p.collectedAmount / p.expectedAmount) * 100)) : null;

const inBand = (pct: number | null, band: ProgressBand) => {
  if (pct === null) return false;
  if (band === 'lt25')   return pct < 25;
  if (band === '25to50') return pct >= 25 && pct < 50;
  if (band === '50to75') return pct >= 50 && pct < 75;
  return pct >= 75;
};

// Amount-based (not months-paid) — church subscriptions are often paid in
// irregular lump sums rather than monthly like rent, so this can't be
// distorted by payment timing the way a "months paid" count would be.
function SubscriptionProgressCell({
  loading, progress,
}: { loading: boolean; progress?: AnbiyamSubscriptionProgress }) {
  if (loading) return <span className="text-xs text-gray-400">…</span>;
  if (!progress || progress.expectedAmount <= 0) return <span className="text-xs text-gray-300">—</span>;

  // The SP already caps each family at what it owes; clamp here too so a
  // stale cached response can never render more than 100%.
  const pct     = Math.min(100, Math.round((progress.collectedAmount / progress.expectedAmount) * 100));
  const pending = Math.max(0, progress.expectedAmount - progress.collectedAmount);
  const barColor = pct >= 75 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-gray-500 mb-0.5">
        <span>{pct}%</span>
        <span>₹{pending.toLocaleString('en-IN')} pending</span>
      </div>
      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

function GraveCount({ loading, progress }: { loading: boolean; progress?: AnbiyamSubscriptionProgress }) {
  if (loading) return <span className="text-xs text-gray-400">…</span>;
  const graves = progress?.graveCount ?? 0;
  const burials = progress?.burialCount ?? 0;
  if (!graves) return <span className="text-xs text-gray-300">0</span>;
  return <span>{graves}{burials > graves && <span className="text-[11px] text-gray-400"> ({burials})</span>}</span>;
}

export default function AnbiyamPage() {
  const { isAdmin } = useAuth();

  // Data
  const [rows,       setRows]       = useState<Anbiyam[]>([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [fetchErr,   setFetchErr]   = useState('');

  // Pagination
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Filters
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Modal
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editTarget,   setEditTarget]   = useState<Anbiyam | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Anbiyam | null>(null);
  const [deleteErr,    setDeleteErr]    = useState('');
  const [deleting,     setDeleting]     = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchErr('');
    try {
      // The progress band is applied in the browser (progress comes from a
      // separate endpoint), so while it's set every Anbiyam is fetched at once.
      const result = await listAnbiyam({
        page:     filters.progress ? 1  : page,
        pageSize: filters.progress ? 50 : pageSize,
        search: filters.search ?? undefined,
        zone:   filters.zone   ?? undefined,
      });
      setRows(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch {
      setFetchErr('Failed to load anbiyam list. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Subscription progress per Anbiyam — fetched independently, once, so a
  // heavier multi-year aggregation never delays the grid itself rendering;
  // this column just fills in (by anbiyam id) whenever it resolves.
  const [subProgress,        setSubProgress]        = useState<Map<number, AnbiyamSubscriptionProgress>>(new Map());
  const [subProgressLoading, setSubProgressLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    getAnbiyamSubscriptionProgress()
      .then((rows) => {
        if (cancelled) return;
        setSubProgress(new Map(rows.map((r) => [r.anbiyamId, r])));
      })
      .catch(() => {/* non-critical — column just stays blank */})
      .finally(() => { if (!cancelled) setSubProgressLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Filter handlers ──────────────────────────────────────────────────────
  const handleFilterChange = (key: string, value: string | number | null) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  // ── Page size ────────────────────────────────────────────────────────────
  const handlePageSize = (s: number) => { setPageSize(s); setPage(1); };

  // ── Create / Edit ────────────────────────────────────────────────────────
  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit   = (row: Anbiyam) => { setEditTarget(row); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };

  const handleSave = async (payload: AnbiyamPayload) => {
    if (editTarget) {
      await updateAnbiyam(editTarget.anbiyamId, payload);
    } else {
      await createAnbiyam(payload);
    }
    closeModal();
    fetchData();
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const confirmDelete = (row: Anbiyam) => { setDeleteTarget(row); setDeleteErr(''); };
  const cancelDelete  = () => { setDeleteTarget(null); setDeleteErr(''); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteErr('');
    try {
      await deleteAnbiyam(deleteTarget.anbiyamId);
      setDeleteTarget(null);
      fetchData();
    } catch (err: any) {
      setDeleteErr(err?.response?.data?.message ?? 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async (format: ExportFormat, scope: ExportScope) => {
    const result = await listAnbiyam({
      page: 1, pageSize: 9999,
      search: scope === 'filtered' ? (filters.search ?? undefined) : undefined,
      zone:   scope === 'filtered' ? (filters.zone   ?? undefined) : undefined,
    });
    exportData({
      title:    'Anbiyam List',
      filename: 'anbiyam-export',
      columns: [
        { label: 'Anbiyam Name',    key: 'anbiyamName'      },
        { label: 'Code',            key: 'anbiyamCode'      },
        { label: 'Zone',            key: 'zone', format: (v) => `Zone ${v}` },
        { label: 'Coordinator',     key: 'coordinatorName'  },
        { label: 'Phone',           key: 'coordinatorPhone' },
        { label: 'Asst. Coordinator', key: 'assCoordinatorName' },
        { label: 'Coordinator Email', key: 'coordinatorEmail'   },
        { label: 'Family Count',    key: 'familyCount'      },
      ],
      rows:    result.items as unknown as Record<string, unknown>[],
      filters: {
        Search: filters.search ?? '',
        Zone:   filters.zone ? `Zone ${filters.zone}` : '',
      },
    }, format);
  };

  const bandRows = filters.progress
    ? rows.filter((r) => inBand(progressPct(subProgress.get(r.anbiyamId)), filters.progress!))
    : rows;
  const { sorted, sort, toggleSort } = useSort(bandRows as unknown as Record<string, unknown>[]);
  const displayRows = sorted as unknown as Anbiyam[];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-navy font-georgia">Anbiyam</h1>
          <p className="text-xs text-teal mt-0.5">Manage zone groups and coordinators</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButton onExport={handleExport} hasFilters={!!(filters.search || filters.zone || filters.progress)} />
          {isAdmin && (
            <button onClick={openCreate} className="btn-primary">
              + Add Anbiyam
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        fields={FILTER_FIELDS}
        values={filters}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      {/* Error */}
      {fetchErr && (
        <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-4 py-2 mb-4">
          {fetchErr}
        </div>
      )}

      {/* Grid — table on tablet/desktop, cards on phones */}
      <div className="hidden sm:block bg-white rounded-lg border border-grid-border shadow-sm overflow-x-auto">
        <table className="w-full text-sm text-navy font-georgia">
          <thead>
            <tr className="bg-navy text-offwhite text-left text-xs uppercase tracking-wide">
              <th className="px-4 py-3">#</th>
              <SortableTh label="Anbiyam Name" sortKey="anbiyamName"      sort={sort} onSort={toggleSort} />
              <SortableTh label="Code"         sortKey="anbiyamCode"      sort={sort} onSort={toggleSort} />
              <SortableTh label="Zone"         sortKey="zone"             sort={sort} onSort={toggleSort} />
              <SortableTh label="Coordinator"  sortKey="coordinatorName"  sort={sort} onSort={toggleSort} />
              <SortableTh label="Phone"        sortKey="coordinatorPhone" sort={sort} onSort={toggleSort} />
              <SortableTh label="Families"     sortKey="familyCount"      sort={sort} onSort={toggleSort} />
              <th className="px-4 py-3 text-center" title="Distinct graves of families in this Anbiyam (burial records in brackets)">Graves</th>
              <th className="px-4 py-3" title="₹ collected vs ₹ expected, all-time since Jan 2023">
                Subscription Progress
              </th>
              {isAdmin && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isAdmin ? 10 : 9} className="text-center py-10 text-teal text-xs">
                  Loading…
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 10 : 9} className="text-center py-10 text-gray-400 text-xs">
                  No anbiyam found.
                </td>
              </tr>
            ) : (
              displayRows.map((row, idx) => (
                <tr
                  key={row.anbiyamId}
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-offwhite/50'}
                >
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {(page - 1) * pageSize + idx + 1}
                  </td>
                  <td className="px-4 py-3 font-medium">{row.anbiyamName}</td>
                  <td className="px-4 py-3 text-teal">{row.anbiyamCode}</td>
                  <td className="px-4 py-3">Zone {row.zone}</td>
                  <td className="px-4 py-3">{row.coordinatorName}</td>
                  <td className="px-4 py-3">{row.coordinatorPhone}</td>
                  <td className="px-4 py-3 text-center">
                    {row.familyCount ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <GraveCount loading={subProgressLoading} progress={subProgress.get(row.anbiyamId)} />
                  </td>
                  <td className="px-4 py-3 min-w-[160px]">
                    <SubscriptionProgressCell
                      loading={subProgressLoading}
                      progress={subProgress.get(row.anbiyamId)}
                    />
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(row)}
                          className="btn-secondary !px-2 !py-1 text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => confirmDelete(row)}
                          className="btn-danger !px-2 !py-1 text-xs"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Grid — compact cards on phones */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <p className="text-center py-10 text-teal text-xs">Loading…</p>
        ) : displayRows.length === 0 ? (
          <p className="text-center py-10 text-gray-400 text-xs">No anbiyam found.</p>
        ) : (
          displayRows.map((row) => (
            <div key={row.anbiyamId} className="bg-white rounded-lg border border-grid-border shadow-sm p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-navy font-georgia truncate">{row.anbiyamName}</p>
                  <p className="text-xs text-teal font-georgia">{row.anbiyamCode}</p>
                </div>
                <span className="flex-shrink-0 text-xs font-bold text-navy bg-offwhite border border-grid-border rounded-full px-2 py-0.5">
                  Zone {row.zone}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-xs text-navy bg-offwhite border border-grid-border rounded px-2 py-0.5">
                  {row.coordinatorName}
                </span>
                <span className="text-xs text-navy bg-offwhite border border-grid-border rounded px-2 py-0.5">
                  {row.coordinatorPhone}
                </span>
                <span className="text-xs text-navy bg-offwhite border border-grid-border rounded px-2 py-0.5">
                  {row.familyCount ?? 0} families
                </span>
                <span className="text-xs text-navy bg-offwhite border border-grid-border rounded px-2 py-0.5">
                  {subProgress.get(row.anbiyamId)?.graveCount ?? 0} graves
                </span>
              </div>
              <div className="mt-2">
                <SubscriptionProgressCell
                  loading={subProgressLoading}
                  progress={subProgress.get(row.anbiyamId)}
                />
              </div>
              {isAdmin && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(row)} className="flex-1 btn-secondary !py-1.5 text-xs">
                    Edit
                  </button>
                  <button onClick={() => confirmDelete(row)} className="flex-1 btn-danger !py-1.5 text-xs">
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination — not used while the progress band filter shows everything at once */}
      {filters.progress ? (
        <p className="text-xs text-gray-500 font-georgia mt-3">{displayRows.length} of {total} Anbiyams in this progress band</p>
      ) : (
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPage={setPage}
          onPageSize={handlePageSize}
        />
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <AnbiyamFormModal
          initial={editTarget}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-base font-semibold text-navy font-georgia mb-2">
              Delete Anbiyam
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete{' '}
              <span className="font-semibold">{deleteTarget.anbiyamName}</span>?
              This cannot be undone.
            </p>
            {deleteErr && (
              <p className="text-red-600 text-sm mb-3">{deleteErr}</p>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={cancelDelete} className="btn-secondary" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="btn-danger" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
